import { useContext, useEffect } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, Input, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { LegacyTransferType } from '../../../../../graphql/schema';
import { calculateCashflows, convertToMoneyWithDefault } from '../../../../shared/helpers/utils';
import { ForecasterPurposeInputsContext, ProjectionInputs } from '../../../context/ForecasterPurposeInputsContext';
import { useRetirementIncomeProjection } from '../../../context/ForecasterPurposeProjectionContext';
import { RetirementIncomeSettingsPurpose } from '../../../data';
import { handleSetupOneTimeDeposit, handleSetupWithdrawal } from '../utils';

const schema = Yup.object().shape({
  monthlyRecurringWithdrawals: Yup.string().optional(),
  adHocInflow: Yup.string().optional(),
});

type Props = {
  purpose: RetirementIncomeSettingsPurpose;
};

type FormikFormProps = {
  recommendedMonthlyWithdrawals?: Money | null;
  otherCashflows?: Money | null;
  updatedFormInputs?: ProjectionInputs['retirementIncome'];
  legacySubAccountId: string;
};

const FormikForm = ({
  recommendedMonthlyWithdrawals,
  otherCashflows,
  updatedFormInputs,
  legacySubAccountId,
}: FormikFormProps) => {
  const { handleSubmit, initialValues, setFieldValue, submitForm, validateForm, values } = useFormikContext<{
    adHocInflow?: string;
    monthlyRecurringWithdrawals?: string;
  }>();

  useEffect(() => {
    void validateForm();
    void submitForm();
  }, [submitForm, validateForm]);

  const shouldShowRecommendedMonthlyWithdrawals =
    recommendedMonthlyWithdrawals &&
    recommendedMonthlyWithdrawals.cents !== updatedFormInputs?.monthlyRecurringWithdrawals?.cents;

  const handleFormChange = async () => {
    await validateForm();
    handleSubmit();
  };

  const handleRecommendedMonthlyWithdawalsClick = async () => {
    if (recommendedMonthlyWithdrawals) {
      await setFieldValue('monthlyRecurringWithdrawals', formatMoney(recommendedMonthlyWithdrawals, 'raw'), true);
      await handleFormChange();
    }
  };

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
        <Field name="monthlyRecurringWithdrawals">
          <Field.Label>Monthly withdrawal</Field.Label>
          <Field.Control>
            <Input mask="number" leadingSymbol="$" placeholder="0" onChange={handleFormChange} />
          </Field.Control>
          {shouldShowRecommendedMonthlyWithdrawals && (
            <Field.Description>
              <Text variant="text50" css={{ mt: 'space.50' }}>
                Recommended:{' '}
                <Button variant="textPrimary" onClick={handleRecommendedMonthlyWithdawalsClick}>
                  {`${formatMoney(recommendedMonthlyWithdrawals)}/month`}
                </Button>
              </Text>
            </Field.Description>
          )}
          <Field.ErrorMessage />
        </Field>
        {values.monthlyRecurringWithdrawals &&
          initialValues.monthlyRecurringWithdrawals !== values.monthlyRecurringWithdrawals && (
            <SambaAutoLink to={handleSetupWithdrawal(legacySubAccountId, values.monthlyRecurringWithdrawals)}>
              Continue to withdrawal
            </SambaAutoLink>
          )}
        <Field name="adHocInflow">
          <Field.Label>One-time contribution</Field.Label>
          <Field.Control>
            <Input mask="number" leadingSymbol="$" placeholder="0" onChange={handleFormChange} />
          </Field.Control>
          <Field.ErrorMessage />
        </Field>
        {values.adHocInflow && initialValues.adHocInflow !== values.adHocInflow && (
          <SambaAutoLink to={handleSetupOneTimeDeposit(legacySubAccountId, values.adHocInflow)}>
            Continue to deposit
          </SambaAutoLink>
        )}
        {otherCashflows && (
          <Text variant="text50">Other planned transactions: {`${formatMoney(otherCashflows)}/month`}</Text>
        )}
      </Box>
    </Form>
  );
};

export const RetirementIncomeSettings: React.FC<Props> = ({ purpose }) => {
  const inputsContext = useContext(ForecasterPurposeInputsContext);
  const { purpose: projectionPurpose } = useRetirementIncomeProjection();
  if (!inputsContext) return null;

  const {
    projectionQueryInputs: { retirementIncome: updatedFormInputs },
    update: { retirementIncomeProjectionInput: updateInputs },
  } = inputsContext;

  const { envelope } = purpose;
  const { legalSubAccounts, monthlyNetRecurringCashflows, recurringTransactionConfigs } = envelope;

  const { initialEditableAmount, otherCashflows } = calculateCashflows({
    monthlyNetRecurringCashflows: monthlyNetRecurringCashflows || toMoney(0),
    legacyTransferType: LegacyTransferType.Withdrawal,
    configs: recurringTransactionConfigs,
  });

  return (
    <Formik
      onSubmit={values => {
        updateInputs({
          monthlyRecurringWithdrawals: convertToMoneyWithDefault(values.monthlyRecurringWithdrawals),
          adHocInflow: convertToMoneyWithDefault(values.adHocInflow),
        });
      }}
      validationSchema={schema}
      initialValues={{
        adHocInflow: '',
        monthlyRecurringWithdrawals: initialEditableAmount ? formatMoney(initialEditableAmount, 'raw') : '',
      }}
    >
      <FormikForm
        recommendedMonthlyWithdrawals={projectionPurpose?.retirementIncomeProjection?.recommendedMonthlyWithdrawals}
        otherCashflows={otherCashflows}
        updatedFormInputs={updatedFormInputs}
        legacySubAccountId={legalSubAccounts[0].legacySubAccountId.toString()}
      />
    </Formik>
  );
};
